import React from "react";
import Badge from "../ui/Badge";
import Button from "../ui/Button";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { submitContactForm } from "../../features/contact/thunks/contact.thunk";
import { resetContactState } from "../../features/contact/slices/contact.slice";
import {
  ContactErrors,
  validateContact,
} from "../../features/contact/utils/validateContact";

type RoleBadgeProps = {
  label: string;
  labelBg: string;
  labelTextColor: string;
  bubbleColor: string;
  side: "left" | "right";
  className?: string;
};

const RoleBadge: React.FC<RoleBadgeProps> = ({
  label,
  labelBg,
  labelTextColor,
  bubbleColor,
  side,
  className = "",
}) => {
  const isLeft = side === "left";

  return (
    <div className={`inline-flex items-center ${className}`}>
      {isLeft ? (
        <>
          <Badge
            className="rounded-[6px] rounded-br-[1px] px-2 py-1 font-title text-[9px] font-medium"
            style={{ backgroundColor: labelBg, color: labelTextColor }}
          >
            {label}
          </Badge>
          <span
            className="ml-1 h-6 w-6 rounded-[999px] rounded-br-[2px]"
            style={{ backgroundColor: bubbleColor }}
          />
        </>
      ) : (
        <>
          <span
            className="h-6 w-6 rounded-[999px] rounded-bl-[2px]"
            style={{ backgroundColor: bubbleColor }}
          />
          <Badge
            className="ml-1 rounded-[6px] rounded-bl-[1px] px-2 py-1 font-title text-[9px] font-medium"
            style={{ backgroundColor: labelBg, color: labelTextColor }}
          >
            {label}
          </Badge>
        </>
      )}
    </div>
  );
};

type FormFieldProps = {
  label: string;
  type?: "text" | "email";
  placeholder: string;
  multiline?: boolean;
  name: string;
  value: string;
  onChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
  error?: string;
};

const FormField: React.FC<FormFieldProps> = ({
  label,
  type = "text",
  placeholder,
  multiline = false,
  name,
  value,
  onChange,
  error,
}) => (
  <div>
    <label className="mb-1 block font-title text-[10px] font-medium text-[#212121]">
      {label}
    </label>
    {multiline ? (
      <textarea
        rows={4}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`w-full rounded-[8px] border bg-white px-3 py-2 font-description text-[10px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none ${
          error ? "border-red-300" : "border-[#E4E4E4]"
        }`}
      />
    ) : (
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        className={`h-9 w-full rounded-[8px] border bg-white px-3 font-description text-[10px] font-light text-[#212121] placeholder:text-[#D9D9D9] focus:outline-none ${
          error ? "border-red-300" : "border-[#E4E4E4]"
        }`}
      />
    )}
    {error && (
      <p className="mt-1 font-description text-[12px] text-red-500">{error}</p>
    )}
  </div>
);

const AboutContactForm: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    isSubmitting,
    isSubmitted,
    error: submitError,
  } = useAppSelector((state) => state.contact);

  const [contactAudience, setContactAudience] = React.useState<
    "personal" | "company"
  >("personal");
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [consent, setConsent] = React.useState(false);
  const [consentError, setConsentError] = React.useState("");
  const [errors, setErrors] = React.useState<ContactErrors>({});

  React.useEffect(() => {
    return () => {
      dispatch(resetContactState());
    };
  }, [dispatch]);

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));

    if (errors[name as keyof ContactErrors]) {
      setErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  const onConsentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setConsent(e.target.checked);
    if (e.target.checked) {
      setConsentError("");
    }
  };

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!consent) {
      setConsentError(
        "You must agree to the Privacy Policy to submit this form.",
      );
      return;
    }
    setConsentError("");

    const validationErrors = validateContact(form);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});

    const result = await dispatch(
      submitContactForm({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        subject: form.subject.trim(),
        message: form.message.trim(),
      }),
    );

    if (submitContactForm.fulfilled.match(result)) {
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setConsent(false);
    }
  };

  return (
    <section className="bg-white py-20">
      <div className="mx-auto max-w-[1120px] px-5">
        <div className="relative mx-auto w-full max-w-[900px] rounded-[30px] border border-[#E1E1E1] bg-white pb-5 shadow-[1px_2px_30px_0_rgba(0,0,0,0.04)] max-[700px]:max-w-[96%]">
          <div className="h-9 w-full rounded-[30px_30px_0_0] border-b border-[#E3E3E3] bg-[#F7F7F7] px-6 pt-3.5">
            <div className="flex items-center gap-1.5">
              <span className="h-2 w-2 rounded-full bg-[#D6D6D6]" />
              <span className="h-2 w-2 rounded-full bg-[#D6D6D6]" />
              <span className="h-2 w-2 rounded-full bg-[#D6D6D6]" />
            </div>
          </div>

          <div className="mt-5 grid grid-cols-[0.85fr_1.2fr_0.85fr] gap-5 px-4 max-[700px]:grid-cols-1">
            <div className="relative pt-2 max-[700px]:hidden">
              <div className="space-y-4">
                <span className="block h-1.5 w-[50%] rounded-full bg-[#F4F4F4]" />
                <span className="block h-1.5 w-[30%] rounded-full bg-[#F4F4F4]" />
                <span className="block h-1.5 w-[44%] rounded-full bg-[#F4F4F4]" />
                <span className="block h-1.5 w-[24%] rounded-full bg-[#F4F4F4]" />
                <span className="block h-1.5 w-[40%] rounded-full bg-[#F4F4F4]" />
                <span className="block h-1.5 w-[26%] rounded-full bg-[#F4F4F4]" />
                <span className="block h-1.5 w-[46%] rounded-full bg-[#F4F4F4]" />
              </div>
            </div>

            <div className="mx-auto w-full max-w-[360px] max-[700px]:max-w-[92%]">
              <div className="flex justify-center">
                <Badge className="inline-flex rounded-full border border-[#E4E4E4] bg-white px-3 py-1 font-description text-[10px] font-normal text-[#B0B0B0]">
                  Get in touch
                </Badge>
              </div>
              <h3 className="mt-3 text-center font-title text-[30px] font-medium leading-[1.08] text-[#212121] max-[700px]:text-[26px]">
                Let&apos;s start a new journey
                <br />
                <span className="font-title text-[#2776EA]">together</span>
              </h3>
              <p className="mx-auto mt-2.5 max-w-[340px] text-center font-description text-[11px] font-light leading-[1.45] text-[#919191]">
                Reach out to us today to explore how our technology solutions
                can drive your business forward. Our team of experts is ready to
                assist you.
              </p>

              <div className="mt-3.5 flex items-center gap-3">
                <Button
                  type="button"
                  onClick={() => setContactAudience("personal")}
                  className={`rounded-full px-3 py-[5px] font-title text-[10px] font-medium ${
                    contactAudience === "personal"
                      ? "bg-[#F7FAFF] text-[#5291EE]"
                      : "text-[#B0B0B0]"
                  }`}
                >
                  For you (Personal)
                </Button>
                <Button
                  type="button"
                  onClick={() => setContactAudience("company")}
                  className={`rounded-full px-3 py-[5px] font-title text-[10px] font-medium ${
                    contactAudience === "company"
                      ? "bg-[#F7FAFF] text-[#5291EE]"
                      : "text-[#B0B0B0]"
                  }`}
                >
                  For your company
                </Button>
              </div>

              <form
                className="mt-3.5 space-y-2.5"
                onSubmit={onSubmit}
                noValidate
              >
                {isSubmitted && (
                  <div className="rounded-[8px] border border-green-200 bg-green-50 px-4 py-3 font-description text-[10px] text-green-700">
                    Thank you! Your message has been sent. We&apos;ll get back
                    to you shortly.
                  </div>
                )}
                {submitError && (
                  <div className="rounded-[8px] border border-red-200 bg-red-50 px-4 py-3 font-description text-[10px] text-red-700">
                    {submitError}
                  </div>
                )}

                <FormField
                  name="name"
                  value={form.name}
                  onChange={onChange}
                  label="Full Name"
                  placeholder="Enter your full name"
                  error={errors.name}
                />
                <FormField
                  name="email"
                  value={form.email}
                  onChange={onChange}
                  label="E-mail address"
                  type="email"
                  placeholder="Enter your email address"
                  error={errors.email}
                />
                <FormField
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  label="Subject"
                  placeholder="Enter your subject line"
                  error={errors.subject}
                />
                <FormField
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  label="Message"
                  placeholder="Enter your message"
                  multiline
                  error={errors.message}
                />

                <div>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={consent}
                      onChange={onConsentChange}
                      className="mt-0.5 h-4 w-4 shrink-0 rounded border border-[#E4E4E4] accent-[#2776EA]"
                    />
                    <span className="font-description text-[10px] font-light leading-[1.5] text-[#616161]">
                      I have read and agree to the{" "}
                      <a
                        href="/privacy-policy"
                        className="text-[#2776EA] underline hover:text-[#1a5cc7]"
                      >
                        Privacy Policy
                      </a>
                      .
                    </span>
                  </label>
                  {consentError && (
                    <p className="mt-1 ml-7 font-description text-[11px] text-red-500">
                      {consentError}
                    </p>
                  )}
                </div>

                <div className="flex justify-end">
                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="rounded-[8px] bg-[#2776EA] px-7 py-[6px] font-title text-[11px] font-medium text-white transition-colors hover:bg-[#1a5cc7] disabled:cursor-not-allowed disabled:bg-[#E4E4E4]"
                  >
                    {isSubmitting ? "Sending..." : "Submit"}
                  </Button>
                </div>
              </form>
            </div>

            <div className="relative pt-2 max-[700px]:hidden">
              <div className="flex justify-end gap-3">
                <span className="block h-1.5 w-[28%] rounded-full bg-[#F7F7F7]" />
                <span className="block h-1.5 w-[22%] rounded-full bg-[#F7F7F7]" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutContactForm;
